import { Injectable } from '@angular/core';
import {
	Data$,
	RemoteData,
	RemoteDataPage,
	appEntities,
	appEntity,
	catchErrorTo,
	filterNil,
} from '@lycoris-nubila/ngx-lycoris';
import { Observable, from, of } from 'rxjs';
import {
	catchError,
	map,
	mapTo,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';

import { IContact } from 'contact/contact';
import { ContactOutput } from 'contact/model/contact.output';
import { ContactConverterPort } from 'contact/port/contact-converter.port';
import { FetchContactsQuery } from 'contact/query/fetch-contacts.query';
import { FetchKanbanContactsQuery } from 'contact/query/fetch-kanban-contacts.query';
import { ObserveKanbanContactsQuery } from 'contact/query/observe-kanban-contacts.query';
import { UserApplicationPort } from 'user/port/user-application.port';

import { AppLocator } from '../../app.locator';
import { AddContactColumnCmd } from './command/add-contact-column.cmd';
import { RemoveContactColumnCmd } from './command/remove-contact-column.cmd';
import { ContactApiPort } from './port/contact-api.port';
import { ContactApplicationPort } from './port/contact-application.port';
import { ContactRepositoryPort } from './port/contact-repository.port';
import { FetchProviderContactsQuery } from './query/fetch-provider-contacts.query';

@Injectable()
export class ContactApplicationAdapter implements ContactApplicationPort {
	constructor(
		private _contactApiPort: ContactApiPort,
		private _contactConverterPort: ContactConverterPort,
		private _contactRepositoryPort: ContactRepositoryPort
	) {}

	private get _userApplicationPort(): UserApplicationPort {
		return AppLocator.inject<UserApplicationPort>(UserApplicationPort);
	}

	public saveContacts(outputs: ContactOutput[]): void {
		this._contactRepositoryPort.saveAll(
			this._contactConverterPort.contactsAsEntities(outputs)
		);
	}

	public fetchContacts(
		query?: FetchContactsQuery
	): Observable<string[] | Error> {
		return this._contactApiPort.fetchContacts(query).pipe(
			tap((contacts) => this.saveContacts(contacts)),
			map((contacts) => contacts.map((contact) => contact.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public fetchKanbanContacts(
		query: FetchKanbanContactsQuery
	): Observable<void | Error> {
		return this._contactApiPort.fetchKanbanContacts(query).pipe(
			tap((contacts) =>
				this._contactRepositoryPort.saveAll(
					this._contactConverterPort.contactsAsEntities(contacts)
				)
			),
			mapTo(void 0),
			catchError((e: Error) => of(e))
		);
	}

	public observeContacts(contactsId?: string[]): Observable<IContact[]> {
		return contactsId
			? this._contactRepositoryPort.observeContacts(contactsId)
			: this._userApplicationPort.observeSessionUserId().pipe(
					filterNil(),
					switchMap((userId) =>
						this._contactRepositoryPort.observeUserContacts(userId)
					)
			  );
	}

	public observeKanbanContacts(
		query: ObserveKanbanContactsQuery
	): Observable<IContact[]> {
		return this._contactRepositoryPort.observeKanbanContacts(
			query.ownerId,
			query.kanbanOrgModuleId
		);
	}

	public addContactColumn(cmd: AddContactColumnCmd): Promise<void> {
		return from(this._contactApiPort.addContactColumn(cmd))
			.pipe(
				tap((contact) =>
					this._contactRepositoryPort.saveAll([
						this._contactConverterPort.contactAsEntity(contact),
					])
				),
				mapTo(null)
			)
			.toPromise();
	}

	public deleteContact(id: string): Promise<void> {
		return from(this._contactApiPort.deleteContact(id))
			.pipe(
				tap(() => this._contactRepositoryPort.deleteContact(id)),
				mapTo(null)
			)
			.toPromise();
	}

	public fetchProviderContacts(
		query: FetchProviderContactsQuery
	): Promise<RemoteDataPage<ContactOutput>> {
		return this._contactApiPort.fetchProviderContacts(query);
	}

	public observeContactsByIds(ids: string[]): Data$<IContact[]> {
		const api$ = from(this._contactApiPort.fetchContactsByIds(ids)).pipe(
			tap((contacts) =>
				this._contactRepositoryPort.saveAll(
					contacts.map((contact) =>
						this._contactConverterPort.contactAsEntity(contact)
					)
				)
			),
			map(
				(contacts) =>
					({
						data: contacts,
						fresh: true,
					} as RemoteData<ContactOutput[]>)
			),
			catchErrorTo({ fresh: true, data: [] }),
			startWith({ fresh: false, data: [] } as RemoteData<ContactOutput[]>)
		);

		const repo$ = this._contactRepositoryPort.observeContacts(ids);

		return appEntities(api$, repo$);
	}

	public observeContact(id: string): Data$<IContact> {
		const api$ = from(this._contactApiPort.fetchContactById(id)).pipe(
			tap((contact) =>
				this._contactRepositoryPort.saveAll([
					this._contactConverterPort.contactAsEntity(contact),
				])
			),
			map((contact) => ({ data: contact, fresh: true })),
			catchErrorTo({ fresh: true, data: null }),
			startWith({ fresh: false, data: null })
		);

		const repo$ = this._contactRepositoryPort.observeContact(id);

		return appEntity(api$, repo$);
	}

	public removeContactColumn(cmd: RemoveContactColumnCmd): Promise<void> {
		return from(this._contactApiPort.removeContactColumn(cmd))
			.pipe(
				tap((contact) =>
					this._contactRepositoryPort.saveAll([
						this._contactConverterPort.contactAsEntity(contact),
					])
				),
				mapTo(null)
			)
			.toPromise();
	}
}
